import dayjs, { Dayjs } from 'dayjs';
import { useMemo } from 'react';
import styled from 'styled-components';

interface ProgressProps {
  type: number;
  startDate: Dayjs;
  endDate: Dayjs;
  createdAt: Dayjs;
  rentalDays: number;
}

const DaysRemainingContainer = styled.div<{
  progress: number;
  type: number;
}>`
  width: 100%;
  .progress-bar-container {
    width: 100%;
    position: relative;
    background: #f2f4f9;
    border-radius: 40px;
    height: 7px;
    margin-top: 8px;
  }
  .progress-bar {
    height: 7px;
    position: absolute;
    background: #1cbaf0;
    border-radius: 40px;
    width: ${(props) => props.progress}%;
  }
  p {
    font-weight: 600;
    font-size: 12px;
    line-height: 15px;
  }
`;

const StatusCard = styled.div<{ backgroundColor: string; color: string }>`
  background: ${(props) => props.backgroundColor};
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;

  p {
    font-weight: 600;
    font-size: 14px;
    line-height: 140%;
    color: ${(props) => props.color};
  }
`;

const Progress = (props: ProgressProps) => {
  const getDaysRemaining = () => {
    const now = dayjs();
    const endDate = props.endDate;

    const totalDays = endDate
      .startOf('day')
      .diff(props.startDate.startOf('day'), 'day', true);

    const daysRemaining = parseFloat(
      props.endDate.diff(now, 'day', true).toFixed(2),
    );

    if (daysRemaining > 0) {
      const percentageOfRemainingDays =
        daysRemaining < 1
          ? (daysRemaining / totalDays) * 100
          : 100 - (daysRemaining / totalDays) * 100;
      return {
        totalDays,
        daysRemaining,
        percentageOfRemainingDays,
      };
    } else {
      return { totalDays: 0, daysRemaining: 0, percentageOfRemainingDays: 0 };
    }
  };

  const getDaysRemainingForFuturePurchase = () => {
    const now = dayjs(new Date());
    const totalDays = Math.ceil(props.startDate.diff(props.createdAt, 'days'));
    const daysRemaining = Math.ceil(props.startDate.diff(now, 'days'));

    if (daysRemaining > 0) {
      const percentageOfRemainingDays = (daysRemaining / totalDays) * 100;
      return {
        totalDays: totalDays,
        daysRemaining,
        percentageOfRemainingDays,
      };
    } else {
      return {
        totalDays: 0,
        daysRemaining: 0,
        percentageOfRemainingDays: 0,
      };
    }
  };

  const daysRemaining = useMemo(() => {
    if (props.type == 0 || props.type == 2) {
      return getDaysRemaining();
    }
  }, [props.type, props.endDate, props.startDate]);

  const futureDaysRemaining = useMemo(() => {
    if (props.type == 1) {
      return getDaysRemainingForFuturePurchase();
    }
  }, [props.type, props.endDate, props.startDate]);

  console.log({ daysRemaining });

  return (
    <>
      {(props.type == 0 || props.type == 2) && daysRemaining.daysRemaining > 0 && (
        <DaysRemainingContainer
          progress={daysRemaining.percentageOfRemainingDays}
          type={props.type}
        >
          <StatusCard
            backgroundColor={
              props.type == 0
                ? 'rgba(243, 192, 92, 0.24)'
                : 'rgba(243, 92, 138, 0.24)'
            }
            color={props.type == 0 ? '#f39c52' : '#f35a82'}
            className="rounded-md"
          >
            {props.type == 0 && (
              <p>
                {daysRemaining.daysRemaining < 1
                  ? `${(daysRemaining.daysRemaining * 24).toFixed(0)} hours`
                  : `${daysRemaining.daysRemaining} days`}{' '}
                remaining
              </p>
            )}
            {props.type == 2 && <p>Expired</p>}
          </StatusCard>
          {props.type == 0 && daysRemaining.daysRemaining > 0 && (
            <div className="progress-bar-container">
              <div className="progress-bar" />
            </div>
          )}
        </DaysRemainingContainer>
      )}
      {props.type == 1 && (
        <DaysRemainingContainer
          progress={futureDaysRemaining.percentageOfRemainingDays}
          type={props.type}
        >
          <StatusCard
            backgroundColor={'rgba(28, 186, 240, 0.24)'}
            color={'#1cb5f0'}
            className="rounded-md"
          >
            <p>
              {futureDaysRemaining.daysRemaining > 1
                ? `${futureDaysRemaining.daysRemaining} days`
                : '1 day'}{' '}
              until rental starts
            </p>
          </StatusCard>

          <div className="progress-bar-container">
            <div className="progress-bar" />
          </div>
        </DaysRemainingContainer>
      )}
    </>
  );
};

export default Progress;
